'use client'

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { Button } from "../buttons";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, ContactDialog } from "../overlay";
import { Menu } from "lucide-react";
import { SessionArea } from "./SessionArea";
import { SessionProvider } from "next-auth/react";
import { PhilaBrandLockup } from "../brand/PhilaBrandLockup";

/**
 * Menu item interface
 */
interface MenuItem {
  name: string
  href: string
  onClick?: () => void
}

const baseMenuItems: MenuItem[] = [
  { name: "About", href: "/about" },
  { name: "Employers", href: "/employers" },
  { name: "Doctors", href: "/doctors" }
]

/**
 * SiteHeader component renders the top navigation bar with responsive menu,
 * brand/logo, links, contact dialog, and authentication buttons.
 * 
 * @returns JSX.Element - the site header navigation bar
 */
export function SiteHeader() {
  const [isOpen, setIsOpen] = useState(false)
  const [isContactOpen, setIsContactOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => setMounted(true), [])

  /**
   * Handles opening and closing of the contact dialog.
   * Also closes the side menu when contact dialog closes.
   *
   * @param open - boolean indicating if contact dialog is open
   */
  const handleContactDialogChange = (open: boolean) => {
    setIsContactOpen(open)
    if (!open) {
      setIsOpen(false)
    }
  }

  const handleMobileNavClick = () => {
    setIsOpen(false)
  }

  const menuItems: MenuItem[] = [
    ...baseMenuItems,
    { name: "Products", href: "/#products", onClick: handleMobileNavClick }
  ]

  return (
    <header className="sticky top-0 z-50 w-full bg-hero-bg">
      <div className="container flex h-[120px] items-center justify-between gap-6 md:gap-12">
        <div className="flex items-center gap-9 lg:gap-12">
          {mounted ? (
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-[60px] w-[60px] hover:bg-secondary/50 focus-visible:bg-secondary/50 focus-visible:ring-2 lg:hidden"
                  aria-label="Toggle Menu"
                >
                  <Menu className="h-9 w-9" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] sm:w-[400px]">
                <SheetTitle>Menu</SheetTitle>
                <nav className="flex flex-col space-y-2 pt-6">
                  {menuItems.map((item) => {
                    if (item.onClick) {
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={item.onClick}
                          className="block px-6 py-[18px] text-xl font-serif font-bold text-hero-text transition-colors hover:bg-accent hover:text-accent-foreground rounded-md"
                        >
                          {item.name}
                        </Link>
                      )
                    }
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        className="block px-6 py-[18px] text-xl font-serif font-bold text-hero-text transition-colors hover:bg-accent hover:text-accent-foreground rounded-md"
                        onClick={() => setIsOpen(false)}
                      >
                        {item.name}
                      </Link>
                    )
                  })}
                  <ContactDialog
                    isOpen={isContactOpen}
                    onOpenChange={handleContactDialogChange}
                    buttonProps={{
                      variant: "ghost",
                      className: "w-full justify-start px-6 py-[18px] text-xl font-serif font-bold text-hero-text"
                    }}
                  />
                  <SessionProvider>
                    <SessionArea />
                  </SessionProvider>
                </nav>
              </SheetContent>
            </Sheet>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-[60px] w-[60px] hover:bg-secondary/50 focus-visible:bg-secondary/50 focus-visible:ring-2 lg:hidden"
              aria-label="Toggle Menu"
              onClick={() => setIsOpen(true)}
            >
              <Menu className="h-9 w-9" />
            </Button>
          )}
          <Link href="/" className="flex items-center group">
            <PhilaBrandLockup variant="default" className="text-brand-dark transition-opacity group-hover:opacity-75" />
          </Link>
        </div>
        
        <nav className="hidden items-center space-x-[18px] text-lg font-serif font-bold text-hero-text lg:flex">
          {menuItems.map((item, index) => {
            const linkElement = item.onClick ? (
              <Link
                key={item.href}
                href={item.href}
                onClick={item.onClick}
                className="transition-colors hover:bg-accent/20 hover:text-accent-foreground rounded-md px-[18px] py-[15px] tracking-wide"
              >
                {item.name}
              </Link>
            ) : (
              <Link
                key={item.href}
                href={item.href}
                className="transition-colors hover:bg-accent/20 hover:text-accent-foreground rounded-md px-[18px] py-[15px] tracking-wide"
              >
                {item.name}
              </Link>
            )
            
            return (
              <React.Fragment key={item.href}>
                {linkElement}
                {index < menuItems.length - 1 && (
                  <div className="h-[30px] w-px bg-hero-text/30" />
                )}
              </React.Fragment>
            )
          })}
          <div className="h-[30px] w-px bg-hero-text/30" />
          {mounted && (
            <ContactDialog
              isOpen={isContactOpen}
              onOpenChange={handleContactDialogChange}
              buttonProps={{
                variant: "ghost",
                className: "h-auto px-[18px] py-[15px] text-lg font-serif font-bold text-hero-text tracking-wide"
              }}
            />
          )}
          <div className="h-[30px] w-px bg-hero-text/30" />
          {mounted && (
            <SessionProvider>
              <SessionArea />
            </SessionProvider>
          )}
        </nav>
      </div>
    </header>
  )
}
